import { defineStore } from 'pinia'

export interface GalleryItem {
  subtitle: string
  title: string
  src?: string
  data?: string
  external: string | null
  /** 'video' items render a <video> player in the lightbox; default is 'image'. */
  type?: 'image' | 'video'
  /** Poster frame shown behind/before video playback. */
  poster?: string
}

export const useLightboxStore = defineStore('lightbox', {
  state: () => ({
    visible: false as boolean,
    index: 0 as number,
    images: [] as GalleryItem[],
    hideTitle: false,
    // Add a flag to prevent double initialization
    isInitialized: false,
  }),
  actions: {
    trigger(vis = false) {
      this.visible = vis
      if (!vis) {
        // Clear state when closing
        this.images = []
        this.index = 0
        this.isInitialized = false
      }
    },
    setImages(newImages: GalleryItem[], index = 0, hideTitle = false) {
      // Prevent duplicate calls with same data
      if (this.isInitialized && this.images.length === newImages.length && this.index === index && this.hideTitle === hideTitle) {
        return
      }

      this.index = Math.max(0, Math.min(index, newImages.length - 1))
      this.hideTitle = hideTitle

      // Simple length check and shallow comparison instead of JSON.stringify
      const imagesChanged =
        this.images.length !== newImages.length ||
        this.images.some((img, i) => img.src !== newImages[i]?.src || img.data !== newImages[i]?.data || img.title !== newImages[i]?.title)

      if (imagesChanged) {
        this.images = [...newImages] // Create shallow copy to prevent mutations
      }

      this.isInitialized = true
      this.trigger(true)
    },

    // Add method to update index without reloading images
    setIndex(newIndex: number) {
      if (newIndex >= 0 && newIndex < this.images.length) {
        this.index = newIndex
      }
    },
  },
})
